// @givmo/compliance — one filing's panel (CCM-R1-INTAKE (a); frames 18, 18-390, 18b, 19, 20;
// DESIGN-NOTE-1b §4 and round 6).
//
// A side sheet at `/compliance/filings/<filing id>` over Filings: when it is next due and why, the
// rule's source under the date, the not-a-substitute sentence under that, the one action for this
// period, the organization's number, and the history as one tile per period with its documents,
// then the filing's corrections and changes in their own list (CCM-R1-INTAKE-HISTORY).
//
// ══ WHERE ROW 7'S SENTENCE GOES, AND WHERE IT NEVER DOES ═══════════════════════════════════════
//
// Under the date block wherever the panel shows a WORKED-OUT date or its basis (D-CCM-19 §3; the
// `obligation-detail` placement): a dated library filing, a licence, a question whose basis shows.
// Never for a filing the library has no date for, a one-time filing already filed, or a filing the
// organization keeps itself (frame 20): nothing there is worked out from law, and the own filing's
// line says so instead. The sentence renders only where the registry serves it (not seeded today).
//
// ══ THE RULE VIEW (design go D2) ═══════════════════════════════════════════════════════════════
//
// The same sheet at `/compliance/filings/<id>/rule`, reached from the panel's source line: the rule
// as transcribed, its source line in the `rule-view` form (the staleness tail, `LAWS_CHANGE_LINE`:
// Q10's one view that keeps it), the official link, and the rule-change notice when flagged. It
// works out no date, so it carries no sentence. An own filing has no rule; its address shows the
// filing.
//
// ══ WHAT IS LEFT OUT (§4.2) ════════════════════════════════════════════════════════════════════
//
// Rule version ids, raw event types, alert-lead arithmetic, fee ladders, how-to-file instructions.
// The form's own name under the title and the government fees are not served yet
// (`CCM-R1-FILING-META-2`, `CCM-BE20`), so they are absent rather than guessed.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  ErrorMessage,
  Field,
  FOCUS_RING,
  Icon,
  IconButton,
  Menu,
  Sheet,
  Skeleton,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { ArrowLeft, Check, ChevronDown, Download, MoreHorizontal } from 'lucide-react';
import { useCompliance } from './compliance-context';
import {
  LinkedRecordLine,
  RECORD_SEARCH_KEY,
  RecordsNamingLine,
  useLinkVendor,
  useVendorRecords,
} from './vendor-records';
import {
  OWN_RECORD_LINE,
  REMINDER_LINE,
  THE_DATE_YOU_RECORDED,
  UNLINK_YOUR_RECORD,
  linkedDate,
  linkedRecordTitle,
  stillRemindsLine,
  vendorTitle,
} from './compliance-vendor-model';
import {
  decodeObligationDetail,
  displayComplianceDate,
  displayLocalDate,
  extractArtifacts,
  extractImportAmendments,
  extractObligationArtifacts,
  extractProfileParameters,
  isModuleUnavailable,
  serverMessage,
  type ComplianceArtifact,
  type ComplianceObligation,
  type ObligationVendorLink,
  type RegistrationImport,
  type RuleProvenance,
} from './compliance-decode';
import { ComplianceDisclosure } from './compliance-disclosure';
import { FilingProvenance } from './review-provenance';
import { RuleChangeNotice, useRuleChange } from './filing-panel-rule-change';
import { nameIfKnown } from './review-model';
import {
  KEEP_TRACKING,
  START_TRACKING_AGAIN,
  STOP_TRACKING,
  STOP_TRACKING_LINE,
  stopTrackingTitle,
  thisRuleUpdatedOn,
  trackingStoppedBy,
  trackingStoppedThroughAi,
} from './review-words';
import { PREPARE_THE_FORM } from './compliance-preparation-model';
import {
  NO_LONGER_TRACKED_LABEL,
  ownFilingStatus,
  type OwnFiling,
  type OwnFilingCadence,
  type OwnFilingCycle,
  type RenewalCycle,
} from './compliance-filings-decode';
import {
  anchorKindFor,
  alsoOpenCycles,
  libraryNextDate,
  ownNextDate,
  ruleShapesOf,
  type FilingGroupView,
  type FilingRow,
  type NextDate,
  overdueCycles,
} from './compliance-filings-model';
import {
  filingGroupsQueryKey,
  useFilingsComposition,
  type FollowThroughIndex,
} from './compliance-filings-reads';
import {
  PREPARE_THE_CORRECTED_FILING,
  SEND_IT_AGAIN,
  followThroughLine,
  followThroughSentence,
  followThroughTileParts,
  isException,
  type FollowThroughLine,
} from './compliance-results-model';
import { CHECK_WHAT_HAPPENED } from './compliance-submission-model';
import {
  addedByLine,
  basisChangeFor,
  beforeFirstSolicitLine,
  cycleBasisLine,
  DATE_PASSED,
  DOCUMENT_ROLE_NAMES,
  earlierYearChoices,
  extensionLine,
  filedAfterWindowClosedLine,
  firstPeriodLine,
  firstPeriodWaitsLine,
  fiscalYearEndedLine,
  isYearKeyed,
  LICENSE_ATTACHED_NOTE,
  lateFeeLead,
  libraryYearTiles,
  longComplianceDate,
  NO_DATE_YET_LINE,
  NOT_WORKED_OUT_YET,
  nothingRecordedLine,
  OVERDUE,
  ownBasisLine,
  ownYearTiles,
  periodLineOf,
  receiptKindFor,
  recordedFiscalYearEnd,
  takesLicenseNote,
  UNTIL_THE_DATE_LINE,
  withRecordedFiles,
  type BasisChange,
  type TileDocument,
  type YearTile,
} from './compliance-panel-model';
import {
  CORRECTIONS_AND_CHANGES,
  correctedFromLine,
  historyEntries,
  importNumberCorrections,
  ownNumberCorrections,
  type HistoryEntry,
} from './compliance-history-model';
import { listAllArtifacts, listAllParameters } from './compliance-queries';
import { COMPLIANCE_ROUTES, FILING_YEAR_SEARCH_KEY, SEND_AGAIN_SEARCH } from './compliance-routes';
import { RULE_VIEW_TITLE, RuleProvenanceLine, sourceRecheckNote } from './compliance-shared';
import { useArtifactDownload } from './artifact-download';
import {
  PrepareButton,
  PrepareLine,
  PreparedFiling,
  usePanelPreparation,
  useYearPreparation,
  type PanelPreparation,
  type YearPreparation,
} from './filing-panel-preparation';
import { PanelDeskRow, usePanelDesk, type PanelDesk } from './filing-desk';
import {
  AnchorDateForm,
  EarlierYearForm,
  FiscalYearEndForm,
  NumberForm,
  RecordFilingForm,
  type NumberTarget,
  type RecordTarget,
} from './filing-panel-forms';
import { useOpenVersion } from './signing-shared';

const PREFIX = 'compliance-filing-panel';

/** How many years show before "Show N earlier years" (round 6). */
const TILES_SHOWN = 3;

/** The own filing's line, in place of the sentence (DESIGN-NOTE-1b §1.3; frame 20). */
export const OWN_FILING_LINE =
  'Givmo’s library doesn’t have this filing, so its dates are the ones you entered. Givmo hasn’t checked them against a source.';

/** A stop's or a restart's failure, as the panel's other record acts say it (the rule change's). */
const RECORD_FAILED = 'We couldn’t record that. Please try again.';

// ── Small parts ────────────────────────────────────────────────────────────────────────────────

function PanelSection({
  title,
  children,
  testId,
}: {
  title: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section className="flex flex-col gap-10" {...testIdProp(testId)}>
      <h3 className="text-s14 font-w600 text-text-strong">{title}</h3>
      {children}
    </section>
  );
}

/**
 * The organization's own pending re-check (CCM-OPS1-WEB; Q3; ruling 7, frame a5): its own line
 * directly under the source line, in the line's own type. It belongs to the date block, so the rule
 * view, which reads the same obligation, shows none. Nothing while none is pending.
 */
function SourceRecheckNote({ provenance }: { provenance: RuleProvenance }) {
  const note = sourceRecheckNote(provenance);
  if (note === undefined) return null;
  return (
    <p className="pt-2 text-s12 text-neutral-600" {...testIdProp(`${PREFIX}-recheck`)}>
      {note}
    </p>
  );
}

function QuietLink({
  children,
  onClick,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-xs text-s13 text-text-secondary underline underline-offset-2 hover:text-text-strong',
        FOCUS_RING,
      )}
      {...testIdProp(testId)}
    >
      {children}
    </button>
  );
}

// ── The year tiles (round 6) ────────────────────────────────────────────────────────────────────

function TileDocumentRow({ document }: { document: TileDocument }) {
  // Downloaded, never previewed: every vault document is stored as an attachment (CCM-WEB2). A file
  // of a package the organization recorded sending is a filing document's version, downloaded by its
  // own logged read (CCM-R1-SUBMIT-HANDOFF).
  const vaultDownload = useArtifactDownload(document.artifactId);
  const openVersion = useOpenVersion();
  const version = document.version;
  const download =
    version !== undefined
      ? { mutate: () => void openVersion(version.documentId, version.versionId) }
      : vaultDownload;
  const role = DOCUMENT_ROLE_NAMES[document.role];
  // A document the organization's list does not name still downloads; it is called by its role
  // rather than by an empty name.
  const named = document.fileName.length > 0;
  return (
    <li
      className="flex items-center gap-12"
      {...testIdProp(`${PREFIX}-document-${document.artifactId}`)}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-12">
        <span className="shrink-0 text-s12 text-text-tertiary sm:w-[104px]">{role}</span>
        {/* The name downloads it, as the icon beside it does. */}
        {named && (
          <button
            type="button"
            onClick={() => download.mutate()}
            className={cn(
              'min-w-0 truncate rounded-xs text-left text-s13 text-text underline decoration-border-strong underline-offset-2',
              FOCUS_RING,
            )}
            {...testIdProp(`${PREFIX}-document-name-${document.artifactId}`)}
          >
            {document.fileName}
          </button>
        )}
      </div>
      <IconButton
        size="sm"
        tone="ghost"
        aria-label={`Download ${named ? document.fileName : role.toLowerCase()}`}
        onClick={() => download.mutate()}
        testId={`${PREFIX}-document-download-${document.artifactId}`}
      >
        <Icon icon={Download} size={15} aria-hidden />
      </IconButton>
    </li>
  );
}

function YearTileView({
  tile,
  confirmationFrom,
  nameOf,
  onAdd,
  onAttach,
  recordForm,
  sendLine,
  step,
}: {
  tile: YearTile;
  confirmationFrom?: string;
  nameOf: (userId: string) => string | undefined;
  /** "Add it", for a writer, on a period with nothing recorded. */
  onAdd?: () => void;
  /** "Attach the confirmation", for a writer, on a period recorded as sent. */
  onAttach?: () => void;
  /** The record form, open in place for this period. */
  recordForm?: ReactNode;
  /**
   * CCM-R1-RESULTS-WEB (D-CCM-68, section 5): what came back after the year's latest send, in the
   * tile's own grammar ("Sent Jan 18, 2027 · …"), in place of the register's send line.
   */
  sendLine?: string;
  /** That line's step, as a link, on a year "Next due" doesn't serve: in place of Attach. */
  step?: { label: string; onClick: () => void };
}) {
  const added = addedByLine(tile, nameOf);
  const actLine =
    tile.act === undefined
      ? undefined
      : tile.act.kind === 'filed'
        ? tile.act.afterWindowClosedOn !== undefined
          ? filedAfterWindowClosedLine(tile.act.on, tile.act.afterWindowClosedOn)
          : `Filed ${displayComplianceDate(tile.act.on)}`
        : confirmationFrom !== undefined
          ? `Sent ${displayComplianceDate(tile.act.on)} · waiting for ${confirmationFrom}’s confirmation`
          : `Sent ${displayComplianceDate(tile.act.on)}`;
  return (
    <li
      className="flex flex-col gap-8 rounded-lg border border-border-subtle px-16 py-12"
      {...testIdProp(`${PREFIX}-tile-${tile.key}`)}
    >
      <div className="flex flex-wrap items-baseline gap-x-10 gap-y-2">
        <span className="text-s15 font-w600 tabular-nums text-text-strong">{tile.title}</span>
        {tile.qualifier !== undefined && (
          <span className="text-s12 text-text-tertiary">{tile.qualifier}</span>
        )}
      </div>
      {tile.act === undefined && sendLine === undefined ? (
        <p className="text-s13 text-text-secondary">
          {tile.covered ??
            nothingRecordedLine(
              tile.target.kind === 'renewal-cycle' ? tile.target.cycle : undefined,
            )}
          {onAdd !== undefined && (
            <>
              {' '}
              <QuietLink onClick={onAdd} testId={`${PREFIX}-tile-add-${tile.key}`}>
                Add it
              </QuietLink>
            </>
          )}
        </p>
      ) : (
        <>
          <p className="text-s13 text-text" {...testIdProp(`${PREFIX}-tile-line-${tile.key}`)}>
            {sendLine ?? actLine}
            {step !== undefined ? (
              <>
                {' · '}
                <QuietLink onClick={step.onClick} testId={`${PREFIX}-tile-step-${tile.key}`}>
                  {step.label}
                </QuietLink>
              </>
            ) : (
              onAttach !== undefined && (
                <>
                  {' · '}
                  <QuietLink onClick={onAttach} testId={`${PREFIX}-tile-attach-${tile.key}`}>
                    Attach the confirmation
                  </QuietLink>
                </>
              )
            )}
          </p>
          {tile.documents.length > 0 && (
            <ul className="-my-2 flex flex-col gap-8 sm:gap-0">
              {tile.documents.map((document) => (
                <TileDocumentRow
                  key={`${document.role}:${document.artifactId}`}
                  document={document}
                />
              ))}
            </ul>
          )}
          {added !== undefined && <p className="text-s12 text-text-tertiary">{added}</p>}
        </>
      )}
      {recordForm}
    </li>
  );
}

// ── The header menu: Copy link, Move to (the row menu's own items), and Stop tracking it ───────

function PanelMenu({
  row,
  current,
  targets,
  canOrganize,
  onMove,
  onNewGroup,
  onStopTracking,
  onUnlink,
}: {
  row: FilingRow;
  current?: FilingGroupView;
  targets: readonly FilingGroupView[];
  canOrganize: boolean;
  onMove: (group: FilingGroupView) => void;
  onNewGroup: () => void;
  /** S1 (REVIEW-19's F3): a tracked library filing someone can change. Opens the confirmation. */
  onStopTracking?: () => void;
  /** L3m (D-CCM-97 (3)): a linked library filing someone can change. It acts at once. */
  onUnlink?: () => void;
}) {
  const movable = canOrganize && !row.readOnly && current?.groupId !== undefined;
  const copyLink = () => {
    const link = new URL(COMPLIANCE_ROUTES.filing(row.ref.id), window.location.origin).href;
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    if (clipboard === undefined) {
      toast.error('We couldn’t copy the link. Please try again.');
      return;
    }
    clipboard.writeText(link).then(
      () => toast.success('Link copied.'),
      () => toast.error('We couldn’t copy the link. Please try again.'),
    );
  };
  return (
    <Menu.Root modal={false}>
      <Menu.Trigger
        saas
        render={<IconButton size="sm" tone="ghost" aria-label="More for this filing" />}
        testId={`${PREFIX}-menu`}
      >
        <Icon icon={MoreHorizontal} size={18} aria-hidden />
      </Menu.Trigger>
      <Menu.Content saas className="min-w-[248px]">
        <Menu.Item saas onClick={copyLink}>
          Copy link
        </Menu.Item>
        {onUnlink !== undefined && (
          <Menu.Item saas onClick={onUnlink} testId={`${PREFIX}-unlink`}>
            {UNLINK_YOUR_RECORD}
          </Menu.Item>
        )}
        {movable && current !== undefined && (
          <>
            <Menu.Separator className="mx-4 my-4 bg-border-subtle" />
            <Menu.Group>
              <Menu.GroupLabel saas>Move to</Menu.GroupLabel>
              <Menu.RadioGroup
                value={current.key}
                onValueChange={(value) => {
                  const target = targets.find((group) => group.key === value);
                  if (target !== undefined && target.key !== current.key) onMove(target);
                }}
              >
                {targets.map((group) => (
                  <Menu.RadioItem
                    key={group.key}
                    saas
                    value={group.key}
                    endIcon={
                      group.key === current.key ? (
                        <Icon icon={Check} size={16} className="text-brand-600" aria-hidden />
                      ) : undefined
                    }
                  >
                    {group.label}
                  </Menu.RadioItem>
                ))}
              </Menu.RadioGroup>
              <Menu.Item saas onClick={onNewGroup}>
                New group…
              </Menu.Item>
            </Menu.Group>
          </>
        )}
        {onStopTracking !== undefined && (
          // Last, under its own line.
          <>
            <Menu.Separator className="mx-4 my-4 bg-border-subtle" />
            <Menu.Item saas onClick={onStopTracking} testId={`${PREFIX}-stop-tracking`}>
              {STOP_TRACKING}
            </Menu.Item>
          </>
        )}
      </Menu.Content>
    </Menu.Root>
  );
}

/** "New group" from the panel: the Filings dialog's words, nested over the sheet. */
function NewGroupFromPanel({
  row,
  open,
  pending,
  onCreate,
  onClose,
}: {
  row: FilingRow;
  open: boolean;
  pending: boolean;
  onCreate: (name: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const filingName =
    row.subtitle !== undefined ? `${row.headline} · ${row.subtitle}` : row.headline;
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {open && (
        <Dialog.Content width={440} className="flex flex-col gap-16" testId={`${PREFIX}-new-group`}>
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            New group
          </Dialog.Title>
          <form
            className="flex flex-col gap-16"
            onSubmit={(event) => {
              event.preventDefault();
              if (trimmed.length > 0) onCreate(trimmed);
            }}
          >
            <Field
              label="Name"
              value={name}
              maxLength={80}
              autoFocus
              onChange={(event) => setName(event.target.value)}
              helper={`${filingName} moves into it. You can move other filings in later.`}
            />
            <div className="flex justify-end gap-10">
              <Button appearance="saas" tone="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                appearance="saas"
                tone="primary"
                type="submit"
                disabled={trimmed.length === 0 || pending}
              >
                Create group
              </Button>
            </div>
          </form>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

/**
 * "Stop tracking it" from the panel's menu (REVIEW-19's F4): S2's title, N40's line, then the act
 * and the way out, as Compliance's other confirmations put them. A refusal shows its served line
 * here (T1, `COMPLIANCE_TRACKING_NOT_ON_CALENDAR`: nothing was changed); any other failure, the
 * panel's own line.
 */
function StopTrackingDialog({
  filingName,
  linkedRecord,
  open,
  pending,
  failure,
  onStop,
  onClose,
}: {
  filingName: string;
  /** D-CCM-97 (choice 9): the current, dated record linked to it, whose reminders go on. */
  linkedRecord?: string;
  open: boolean;
  pending: boolean;
  failure: unknown;
  onStop: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
    >
      {open && (
        <Dialog.Content
          width={440}
          className="flex flex-col gap-16"
          testId={`${PREFIX}-stop-dialog`}
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {stopTrackingTitle(filingName)}
          </Dialog.Title>
          <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {STOP_TRACKING_LINE}
            {linkedRecord !== undefined && ` ${stillRemindsLine(linkedRecord)}`}
          </Dialog.Description>
          {failure !== null && failure !== undefined && (
            <ErrorMessage testId={`${PREFIX}-stop-failure`}>
              {serverMessage(failure, RECORD_FAILED)}
            </ErrorMessage>
          )}
          <div className="flex flex-wrap gap-8">
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              loading={pending}
              onClick={onStop}
              testId={`${PREFIX}-stop-confirm`}
            >
              {STOP_TRACKING}
            </Button>
            <Button
              appearance="saas"
              tone="ghost"
              size="md"
              disabled={pending}
              onClick={onClose}
              testId={`${PREFIX}-stop-keep`}
            >
              {KEEP_TRACKING}
            </Button>
          </div>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

// ── What the panel shows, composed ─────────────────────────────────────────────────────────────

type OpenForm =
  | {
      kind: 'record';
      target: RecordTarget;
      periodLine?: string;
      initial: 'confirmed' | 'sent';
      /** The year it was opened from: the form opens in that year's tile, where the person is. */
      tileKey?: string;
    }
  | { kind: 'number' }
  | { kind: 'change'; change: BasisChange }
  | { kind: 'earlier' };

interface NextDueView {
  /** The lead: a long date, or the words for a period with no date. */
  lead: string;
  /**
   * CCM-BE24-B (W2): where another year is open beside this one, the year this one covers ("For the
   * fiscal year ended …", with Y6 on a short year), so the two cards are told apart. A first year a
   * sending dates says it too, beside F1 (D-CCM-110 J-F).
   */
  yearLine?: string;
  /** MDEXT-OPEN: N1 after the date, while the year is held open by its automatic extension. */
  leadNote?: string;
  /** Whether the lead is a date (it carries the date's weight). */
  dated: boolean;
  basis?: string;
  change?: BasisChange;
  /** A quiet second line: the extension, or the send waiting for its confirmation. */
  aside?: string;
  /**
   * CCM-R1-RESULTS-WEB: the aside says an exception came back after the year's latest send, at body
   * weight (D-CCM-68, section 5), so it never passes for routine.
   */
  asideException?: boolean;
  /**
   * The step after an exception, in the action's place: Send it again (the send page's review) or
   * Prepare the corrected filing (the form, as "Open the form" opens it).
   */
  step?: { label: string; go: 'send-again' | 'prepare-corrected' };
  /** The year the block's line speaks for: its tile then carries no second door. */
  servedCycleId?: string;
  /** Lines at the basis's size under the lead: a stopped filing's S3 and N40's line (D-CCM-58). */
  lines?: string[];
  /**
   * CCM-MOD5B (V8b): a library filing Givmo has no date for names the organization's records of its
   * kind in its jurisdiction, under its lead (D-CCM-94 (4)).
   */
  namesRecords?: boolean;
  /**
   * CCM-MOD5-RECORD-FILING-LINK (L3): the organization's record this filing is linked to, named in
   * V8b's place in every state.
   */
  linked?: ObligationVendorLink;
  /**
   * The lead is that record's date (D-CCM-97 (2), fill, never override): its basis's Change opens
   * the record, and the record's own-record line follows its linked line.
   */
  recordDate?: boolean;
  /** A library filing whose tracking stopped: S4 starts it again (an own filing's retire stays). */
  restartable?: boolean;
  provenance?: RuleProvenance;
  /** Whether row 7's sentence goes under the block. */
  sentence: boolean;
  /** The block's question, in place of an action (a date only the organization has). */
  question?: {
    kind: 'license-expiry' | 'registration-date' | 'fiscal-year-end';
    /** A line under the field (frame 04: the license is attached, but nothing reads it). */
    note?: string;
  };
  action?: {
    label: string;
    target: RecordTarget;
    initial: 'confirmed' | 'sent';
    periodLine?: string;
  };
}

/** A Next due view without its basis line and Change (W2: they go on the newest open year). */
function withoutBasis(view: NextDueView): NextDueView {
  const copy = { ...view };
  delete copy.basis;
  delete copy.change;
  return copy;
}

function libraryNextDue(
  obligation: ComplianceObligation,
  next: NextDate,
  cycles: readonly RenewalCycle[],
  fiscalYearEnd: string | undefined,
  licenseOnFile: boolean,
  /** S3, for a filing whose tracking stopped: who stopped it, and when. */
  stoppedLine?: string,
): NextDueView {
  const jurisdiction = obligation.jurisdictionName || obligation.jurisdictionCode;
  const waiting = cycles.find(
    (cycle) =>
      cycle.isCurrent && cycle.dueOn === undefined && cycle.uncomputableReason !== undefined,
  );
  const basisOf = (cycle: RenewalCycle | undefined): string | undefined =>
    cycleBasisLine(obligation, cycle, fiscalYearEnd);
  const obligationTarget = (recurring: boolean): RecordTarget => ({
    kind: 'obligation',
    obligation,
    receiptKind: receiptKindFor(obligation.obligationClass, recurring),
  });
  switch (next.kind) {
    case 'dated': {
      const cycle = next.cycle;
      const sentOn = cycle?.submissionRecordedOn;
      const target: RecordTarget =
        cycle !== undefined
          ? {
              kind: 'renewal-cycle',
              cycle,
              receiptKind: receiptKindFor(obligation.obligationClass, isYearKeyed(cycle.cycleKey)),
            }
          : obligationTarget(false);
      return {
        lead: longComplianceDate(next.dueOn),
        ...(next.datePassed === true ? { leadNote: DATE_PASSED } : {}),
        dated: true,
        basis: basisOf(cycle),
        change: cycle !== undefined ? basisChangeFor(cycle.dueDateBasis) : undefined,
        aside:
          sentOn !== undefined
            ? `Sent ${displayComplianceDate(sentOn)}, waiting for ${jurisdiction}’s confirmation.`
            : extensionLine(cycle?.extension),
        provenance: cycle?.provenance ?? obligation.provenance,
        sentence: true,
        action:
          sentOn !== undefined
            ? {
                label: 'Attach the confirmation',
                target,
                initial: 'confirmed',
                periodLine: periodLineOf(cycle),
              }
            : {
                label: 'Record this filing',
                target,
                initial: 'confirmed',
                periodLine: periodLineOf(cycle),
              },
      };
    }
    case 'needs-date': {
      const expiry = anchorKindFor(next.dueDateBasis) === 'license_expiry';
      return {
        lead: NOT_WORKED_OUT_YET,
        dated: false,
        basis: basisOf(waiting),
        aside: UNTIL_THE_DATE_LINE,
        provenance: waiting?.provenance ?? obligation.provenance,
        sentence: true,
        question: expiry
          ? {
              kind: 'license-expiry',
              // Frame 04's note: D.C.'s licence only, the sheet's own rule (`takesLicenseNote`).
              ...(licenseOnFile && takesLicenseNote(obligation.jurisdictionCode, next.dueDateBasis)
                ? { note: LICENSE_ATTACHED_NOTE }
                : {}),
            }
          : { kind: 'registration-date' },
      };
    }
    case 'needs-fiscal-year-end':
      return {
        lead: NOT_WORKED_OUT_YET,
        dated: false,
        provenance: obligation.provenance,
        sentence: true,
        question: { kind: 'fiscal-year-end' },
      };
    case 'before-first-solicitation': {
      // Once the register records the send, the filing made once reads as a dated one does
      // (CCM-R1-SUBMIT-WEB; slice 3's built-panel note, frames 67 and 81): the send waiting for
      // its confirmation, and Attach the confirmation, in frames 18 and 19's words.
      const sentOn = obligation.submissionRecordedOn;
      return {
        lead: beforeFirstSolicitLine(jurisdiction),
        dated: false,
        ...(sentOn !== undefined
          ? {
              aside: `Sent ${displayComplianceDate(sentOn)}, waiting for ${jurisdiction}’s confirmation.`,
            }
          : {}),
        provenance: obligation.provenance,
        sentence: true,
        action: {
          label: sentOn !== undefined ? 'Attach the confirmation' : 'Record this filing',
          target: obligationTarget(false),
          initial: 'confirmed',
        },
      };
    }
    case 'filed-once':
      // Jonathan's ruling (2026-09-30 22:46 PDT): its source line under "Filed once.", as the
      // no-date case has, so the rule's own view is one click away.
      return {
        lead: 'Filed once.',
        dated: false,
        provenance: obligation.provenance,
        sentence: false,
      };
    case 'not-supported':
      // Jonathan's ruling (2026-09-30): no date, but still the rule's source line under the words,
      // so the rule's own view stays one click away. Nothing is worked out, so no sentence.
      return {
        lead: NO_DATE_YET_LINE,
        dated: false,
        provenance: obligation.provenance,
        sentence: false,
        action: { label: 'Record a filing', target: obligationTarget(false), initial: 'confirmed' },
        namesRecords: true,
      };
    case 'first-period-waits':
      // D-CCM-110 J-F: no year is dated until the registration statement's sending is recorded, on
      // the registration (C1-b, D-CCM-112). Choice 3 (A): an annual report filed or sent, recorded
      // here, lands on its year and starts the calendar there, as on any filing with no date.
      return {
        lead: NOT_WORKED_OUT_YET,
        dated: false,
        lines: [firstPeriodWaitsLine(jurisdiction)],
        provenance: obligation.provenance,
        sentence: false,
        action: { label: 'Record a filing', target: obligationTarget(true), initial: 'confirmed' },
      };
    case 'no-longer-tracked':
      // D-CCM-58 (U15, REVIEW-19's F2): "No longer tracked", who stopped it and when (S3), N40's
      // line, and the rule's source line, as the panel's other no-date filings keep it. Under the
      // block, "Start tracking it again" (S4) for someone who can make changes.
      return {
        lead: NO_LONGER_TRACKED_LABEL,
        dated: false,
        sentence: false,
        lines: [...(stoppedLine !== undefined ? [stoppedLine] : []), STOP_TRACKING_LINE],
        provenance: obligation.provenance,
        restartable: true,
      };
  }
}

/**
 * "Next due" by what came back after the year's latest send (CCM-R1-RESULTS-WEB; D-CCM-68, section
 * 5, frames e1–e5): its line in place of "Sent …, waiting for …’s confirmation.", an exception at
 * body weight with its step in the action's place. An uncertain send's step is "This year’s
 * filing"'s Check what happened, so the block offers none.
 */
function withFollowThrough(
  view: NextDueView,
  line: FollowThroughLine | undefined,
  jurisdiction: string,
  cycleId: string | undefined,
): NextDueView {
  if (line === undefined) return view;
  const followed: NextDueView = {
    ...view,
    aside: followThroughSentence(line, jurisdiction),
    asideException: isException(line),
    ...(cycleId !== undefined ? { servedCycleId: cycleId } : {}),
  };
  switch (line.kind) {
    case 'not-delivered':
      return { ...followed, action: undefined, step: { label: SEND_IT_AGAIN, go: 'send-again' } };
    case 'answer':
      return {
        ...followed,
        action: undefined,
        step: { label: PREPARE_THE_CORRECTED_FILING, go: 'prepare-corrected' },
      };
    case 'uncertain':
      return { ...followed, action: undefined };
    case 'waiting':
      return followed;
  }
}

function ownNextDue(filing: OwnFiling, next: NextDate, editable: boolean): NextDueView {
  const open = filing.latestCycle;
  const basis = ownBasisLine(filing.cadence, filing.cadenceInterval);
  if (next.kind === 'dated') {
    return {
      lead: longComplianceDate(next.dueOn),
      dated: true,
      basis,
      change: editable ? 'own-date' : undefined,
      sentence: false,
      ...(editable && open !== undefined
        ? {
            action: {
              label: 'Record this filing',
              target: { kind: 'own-cycle' as const, filingId: filing.filingId, cycle: open },
              initial: 'confirmed' as const,
            },
          }
        : {}),
    };
  }
  if (next.kind === 'no-longer-tracked')
    return { lead: NO_LONGER_TRACKED_LABEL, dated: false, sentence: false };
  if (next.kind === 'filed-once') return { lead: 'Filed once.', dated: false, sentence: false };
  return { lead: '—', dated: false, sentence: false };
}

// ── The panel ──────────────────────────────────────────────────────────────────────────────────

export interface FilingPanelProps {
  filingId: string;
  /** What the sheet shows: the filing (the default), or the rule behind it (design go D2). */
  view?: 'filing' | 'rule';
  /** Close the panel: back to what it was opened over. */
  onClose: () => void;
  /** Go to another of the module's addresses in place; absent, the rule view is not offered. */
  onNavigate?: (to: string, search?: Readonly<Record<string, string>>) => void;
}

/** A link to one of the module's addresses: its href, and how to follow it in place. */
interface InPlaceLink {
  href: string;
  onFollow: () => void;
}

/** An `<a>` with a real address that a plain click follows in place (a modified one, as browsers do). */
function InPlaceAnchor({
  link,
  children,
  className,
  testId,
}: {
  link: InPlaceLink;
  children: ReactNode;
  className?: string;
  testId?: string;
}) {
  return (
    <a
      href={link.href}
      onClick={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return;
        event.preventDefault();
        link.onFollow();
      }}
      className={className}
      {...testIdProp(testId)}
    >
      {children}
    </a>
  );
}

/** The rule behind a library filing (design go D2): what Givmo transcribed, and from where. */
function RuleView({ obligation }: { obligation: ComplianceObligation }) {
  // N36 alone: what changed, and the act, are the filing's own view's (frame 103).
  const ruleChange = useRuleChange(obligation);
  return (
    <div className="flex flex-col gap-16" {...testIdProp('compliance-rule-view')}>
      {ruleChange.flagged && !ruleChange.pending && ruleChange.day !== undefined && (
        <p
          className="rounded-lg border border-border-subtle bg-surface-sunken px-16 py-12 text-s13 text-text"
          {...testIdProp(`${PREFIX}-rule-changed`)}
        >
          {thisRuleUpdatedOn(ruleChange.day)}
        </p>
      )}
      {obligation.ruleSummary.length > 0 && (
        <p
          className="text-s14 leading-h15 text-pretty text-text"
          {...testIdProp('compliance-rule-summary')}
        >
          {obligation.ruleSummary}
        </p>
      )}
      {/* The rule's own view (Q10): the one place the staleness line stays. */}
      <RuleProvenanceLine provenance={obligation.provenance} form="rule-view" />
    </div>
  );
}

export function FilingPanel({ filingId, view = 'filing', onClose, onNavigate }: FilingPanelProps) {
  const { orgRef, isReady, operations, moduleStanding, canWrite, personName, organizationName } =
    useCompliance();
  const ref = isReady && moduleStanding === 'available' ? orgRef : null;
  const composition = useFilingsComposition(operations, ref);
  const queryClient = useQueryClient();
  const canOrganize = canWrite === true;
  const [form, setForm] = useState<OpenForm | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [stopOpen, setStopOpen] = useState(false);

  const row = composition.rows?.find((candidate) => candidate.ref.id === filingId);
  const group = composition.groups?.find((candidate) => candidate.rows.includes(row as FilingRow));
  const records = composition.records;
  const obligation =
    row?.ref.kind === 'library'
      ? records?.obligations.find((candidate) => candidate.obligationId === filingId)
      : undefined;
  const ownFiling =
    row?.ref.kind === 'organization_recorded'
      ? records?.ownFilings.find((candidate) => candidate.filingId === filingId)
      : undefined;

  // The vault names who added a document; an obligation's own documents name the row's.
  const vault = useQuery({
    queryKey: ['compliance', 'artifacts', ref],
    queryFn: () => listAllArtifacts(operations, ref ?? ''),
    enabled: ref !== null && row !== undefined,
  });
  const obligationDocuments = useQuery({
    queryKey: ['compliance', 'obligation-artifacts', ref, filingId],
    queryFn: () => operations.listObligationArtifacts(ref ?? '', filingId),
    enabled: ref !== null && obligation !== undefined,
  });
  // "Corrections and changes" (CCM-R1-INTAKE-HISTORY): the number's corrections, read only when the
  // import carries some; the filing's own log, for its changed dates; and every fiscal year end
  // recorded, superseded ones included. Each under the key its other readers already use.
  const importRow: RegistrationImport | undefined =
    obligation !== undefined
      ? records?.imports.find((candidate) => candidate.obligationId === obligation.obligationId)
      : undefined;
  const corrected = importRow !== undefined && importRow.amendmentCount > 0;
  const amendments = useQuery({
    queryKey: ['compliance', 'import-amendments', ref, importRow?.importId],
    queryFn: () =>
      operations.listRegistrationImportAmendments(ref ?? '', importRow?.importId ?? ''),
    enabled: ref !== null && corrected,
  });
  const detail = useQuery({
    queryKey: ['compliance', 'obligation', ref, filingId],
    queryFn: () => operations.getObligation(ref ?? '', filingId),
    enabled: ref !== null && obligation !== undefined,
  });
  const parameters = useQuery({
    queryKey: ['compliance', 'profile-parameters', ref],
    queryFn: () => listAllParameters(operations, ref ?? ''),
    enabled: ref !== null && obligation !== undefined,
  });
  // CCM-R1-DOCS: the form Givmo prepares for this filing, for the year its next date names.
  const libraryNext = useMemo(
    () =>
      obligation !== undefined && records !== undefined
        ? libraryNextDate(
            obligation,
            records.cycles.filter((cycle) => cycle.obligationId === obligation.obligationId),
            records.occurrences.filter((o) => o.obligationId === obligation.obligationId),
            records.profile,
            records.today,
            ruleShapesOf(records.ruleShapes, obligation),
          )
        : undefined,
    [obligation, records],
  );
  const preparation = usePanelPreparation(obligation, libraryNext, (to) => onNavigate?.(to));
  // CCM-R1-DESK-WEB: Givmo's filing desk on the same year ("Who files it", its status and acts).
  const desk = usePanelDesk(obligation, libraryNext);
  // CCM-R1-LATE-FILINGS: a year read overdue is prepared from its own card, on its own year.
  const yearPreparation = useYearPreparation(obligation, (to, search) => onNavigate?.(to, search));

  const moveFiling = useMutation({
    mutationFn: (target: FilingGroupView) =>
      operations.moveFilingToGroup(ref ?? '', target.groupId ?? '', {
        filing_kind: row?.ref.kind ?? 'library',
        filing_id: filingId,
      }),
    onSuccess: (_body, target) => {
      toast.success(`Moved to ${target.label}.`);
      void queryClient.invalidateQueries({ queryKey: filingGroupsQueryKey(ref) });
    },
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t move that filing. Please try again.')),
  });
  const createGroup = useMutation({
    mutationFn: (name: string) =>
      operations.createFilingGroup(ref ?? '', {
        name,
        filing_kind: row?.ref.kind ?? 'library',
        filing_id: filingId,
      }),
    onSuccess: () => {
      toast.success('Group created.');
      setNewGroupOpen(false);
      void queryClient.invalidateQueries({ queryKey: filingGroupsQueryKey(ref) });
    },
    onError: (cause) =>
      toast.error(serverMessage(cause, 'We couldn’t create that group. Please try again.')),
  });
  // CCM-R1-TRACKING-PANEL (D-CCM-58): both acts move the filing's dates and years, so every
  // compliance read is read again: the Filings page, the Calendar and the review among them. The
  // confirmation closes once the panel shows the stopped filing (F2); the restart shows its next
  // due again at once, with no toast (F6).
  const stopTracking = useMutation({
    mutationFn: () => operations.stopTracking(ref ?? '', filingId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['compliance'] });
      setStopOpen(false);
    },
  });
  const restartTracking = useMutation({
    mutationFn: () => operations.resumeTracking(ref ?? '', filingId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['compliance'] }),
  });
  // CCM-MOD5-RECORD-FILING-LINK: unlinking from the menu (L3m), at once; and every record's title,
  // for the link entries in "Corrections and changes".
  const linkRecord = useLinkVendor();
  const vendorList = useVendorRecords().data;
  const vendorLink = obligation?.vendorLink;
  const unlink =
    canOrganize && vendorLink !== undefined
      ? () =>
          linkRecord.mutate({ vendorId: vendorLink.vendorId, obligationId: filingId, unlink: true })
      : undefined;

  const nameOf = (userId: string) => personName?.(userId);
  // Who recorded what came back after a send (CCM-R1-RESULTS-WEB).
  const sentBy = (userId: string | undefined) =>
    userId !== undefined ? personName?.(userId) : undefined;
  const today = records?.today ?? '';
  const link = (to: string): InPlaceLink | undefined =>
    onNavigate !== undefined ? { href: to, onFollow: () => onNavigate(to) } : undefined;
  // The rule view exists only for a filing with a rule: a library filing.
  const ruleLink =
    obligation !== undefined ? link(COMPLIANCE_ROUTES.filingRule(filingId)) : undefined;
  const showRule = view === 'rule' && obligation !== undefined;

  // The history's reads hold the panel as its other reads do: a partial history is never shown.
  const loading =
    composition.loading ||
    (row !== undefined && vault.isPending) ||
    (obligation !== undefined &&
      (obligationDocuments.isPending ||
        detail.isPending ||
        parameters.isPending ||
        preparation.loading)) ||
    (corrected && amendments.isPending);
  const failed =
    composition.failed ??
    vault.error ??
    obligationDocuments.error ??
    detail.error ??
    parameters.error ??
    amendments.error ??
    undefined;

  let body: ReactNode;
  let title = '';
  if (failed !== undefined && failed !== null) {
    body = (
      <p className="text-s14 text-text-secondary" {...testIdProp(`${PREFIX}-error`)}>
        {isModuleUnavailable(failed)
          ? 'Compliance isn’t part of this environment yet.'
          : serverMessage(failed, 'We couldn’t load this filing. Please try again.')}
      </p>
    );
  } else if (
    loading ||
    records === undefined ||
    // A filing just added is not missing while the lists naming the filings are read again.
    (row === undefined && composition.refreshing === true)
  ) {
    body = <Skeleton className="h-[320px] w-full" {...testIdProp(`${PREFIX}-loading`)} />;
  } else if (row === undefined) {
    body = (
      <p className="text-s14 text-text-secondary" {...testIdProp(`${PREFIX}-missing`)}>
        We couldn’t find that filing. Please try again.
      </p>
    );
  } else {
    title = row.subtitle !== undefined ? `${row.headline} · ${row.subtitle}` : row.headline;
    const vaultArtifacts: ComplianceArtifact[] = extractArtifacts(vault.data);
    let next: NextDueView;
    let alsoOpen: RenewalCycle[] = [];
    let tiles: YearTile[];
    let numberLabel: string;
    let number: string | undefined;
    let numberTarget: NumberTarget | undefined;
    let earlier:
      | { kind: 'library'; deadlineIndex: number; years: number[] }
      | { kind: 'own' }
      | undefined;
    if (obligation !== undefined) {
      const cycles = records.cycles.filter(
        (cycle) => cycle.obligationId === obligation.obligationId,
      );
      const occurrences = records.occurrences.filter(
        (o) => o.obligationId === obligation.obligationId,
      );
      const fye = recordedFiscalYearEnd(records.profile);
      // CCM-BE24-B: a confirmed filing's years count from the year end, so moving it asks Y1.
      const yearEndAsks =
        records.profile?.yearEndChangeRequired === true
          ? { organization: organizationName ?? '', today }
          : undefined;
      // The files the organization recorded sending itself join their year (CCM-R1-SUBMIT-HANDOFF).
      tiles = withRecordedFiles(
        libraryYearTiles({
          obligation,
          cycles,
          occurrences,
          obligationDocuments: extractObligationArtifacts(obligationDocuments.data),
          vault: vaultArtifacts,
          today,
        }),
        preparation.handoff,
        vaultArtifacts,
        preparation.cycleId !== undefined ? { cycleId: preparation.cycleId } : {},
      );
      // The license is on file when the record carries its confirmation, on the filing or a year.
      const licenseOnFile =
        obligation.confirmationArtifactId !== undefined ||
        tiles.some((tile) => tile.documents.some((document) => document.role === 'confirmation'));
      const nextDate = libraryNextDate(
        obligation,
        cycles,
        occurrences,
        records.profile,
        today,
        ruleShapesOf(records.ruleShapes, obligation),
      );
      // CCM-BE24-B (W2): a second year held open beside the first, on the day the year end changed.
      alsoOpen = alsoOpenCycles(obligation, cycles, occurrences, nextDate, today);
      // CCM-R1-RESULTS-WEB: what came back after the year's latest send (D-CCM-68, section 5).
      const nextCycleId = nextDate.kind === 'dated' ? nextDate.cycle?.cycleId : undefined;
      next = withFollowThrough(
        libraryNextDue(
          obligation,
          nextDate,
          cycles,
          fye,
          licenseOnFile,
          obligation.trackingStoppedAt === undefined
            ? undefined
            : // D-CCM-96 (frame A3): a stop made through the person's connected AI says so.
              obligation.trackingStoppedChannel === 'connector'
              ? trackingStoppedThroughAi(
                  nameIfKnown(nameOf, obligation.trackingStoppedByUserId),
                  displayLocalDate(obligation.trackingStoppedAt),
                )
              : trackingStoppedBy(
                  nameIfKnown(nameOf, obligation.trackingStoppedByUserId),
                  displayLocalDate(obligation.trackingStoppedAt),
                ),
        ),
        nextDate.kind === 'dated' || nextDate.kind === 'before-first-solicitation'
          ? followThroughLine(
              composition.followThrough?.forYear(obligation.obligationId, nextCycleId),
              sentBy,
            )
          : undefined,
        obligation.jurisdictionName || obligation.jurisdictionCode,
        nextCycleId,
      );
      if (alsoOpen.length > 0 && nextDate.kind === 'dated' && nextDate.cycle !== undefined) {
        // W2: the basis line and its Change name the year end in force, so they go on the newest
        // open year's card; this one, the oldest, says the year it covers instead.
        const yearLine = fiscalYearEndedLine(nextDate.cycle);
        next = { ...withoutBasis(next), ...(yearLine !== undefined ? { yearLine } : {}) };
      }
      // F1 (D-CCM-112): the first year a sending dates (the served `first_period`, D-CCM-110 J-F)
      // says which year it is and how Givmo counted it; any other year's card is as it was.
      const nextCycle = nextDate.kind === 'dated' ? nextDate.cycle : undefined;
      if (
        nextCycle !== undefined &&
        occurrences.some(
          (occurrence) =>
            occurrence.cycleKey === nextCycle.cycleKey &&
            occurrence.deadlineIndex === nextCycle.deadlineIndex &&
            occurrence.firstPeriod?.first === true,
        )
      ) {
        const yearLine = next.yearLine ?? fiscalYearEndedLine(nextCycle);
        next = {
          ...next,
          ...(yearLine !== undefined ? { yearLine } : {}),
          lines: [
            ...(next.lines ?? []),
            firstPeriodLine(obligation.jurisdictionName || obligation.jurisdictionCode),
          ],
        };
      }
      // CCM-MOD5-RECORD-FILING-LINK (D-CCM-97 (2), L3): a linked record's date fills a filing Givmo
      // has no date for, while it is tracked and the record current and dated; no row 7 sentence,
      // since the date is the organization's own. Its linked line stands in V8b's place whatever
      // the state: undated, ended, stopped, or a date of Givmo's (the design stop's 4.4 (d), (e)).
      if (vendorLink !== undefined) {
        const filled = linkedDate(
          vendorLink,
          nextDate.kind === 'not-supported',
          obligation.trackingStoppedAt !== undefined,
        );
        next =
          filled.kind === 'dated'
            ? {
                lead: longComplianceDate(filled.dueOn),
                dated: true,
                basis: THE_DATE_YOU_RECORDED,
                aside: REMINDER_LINE,
                provenance: obligation.provenance,
                sentence: false,
                ...(next.action !== undefined ? { action: next.action } : {}),
                linked: vendorLink,
                recordDate: true,
              }
            : { ...next, linked: vendorLink };
      }
      numberLabel = obligation.numberLabel ?? 'Registration number';
      number = importRow?.amendedRegistrationNumber ?? importRow?.recordedRegistrationNumber;
      // Ruling 8: the corrected number keeps what was first entered beside it, and the corrections
      // and changes follow the years.
      const correctedFrom = correctedFromLine(importRow?.recordedRegistrationNumber, number);
      const history = historyEntries({
        numberLabel,
        numberCorrections: corrected
          ? importNumberCorrections(extractImportAmendments(amendments.data))
          : [],
        fiscalYearEnds: extractProfileParameters(parameters.data),
        dueDateBases: [
          ...cycles.map((cycle) => cycle.dueDateBasis),
          ...occurrences.map((occurrence) => occurrence.dueDateBasis),
        ],
        addedAt: obligation.confirmedAt,
        events: decodeObligationDetail(detail.data).events,
        nameOf,
        // The link entries name the record as Filings does; the open link's own read names it too.
        recordTitle: (vendorId) => {
          const listed = vendorList?.find((record) => record.vendorId === vendorId);
          if (listed !== undefined) return vendorTitle(listed);
          return vendorLink?.vendorId === vendorId ? linkedRecordTitle(vendorLink) : undefined;
        },
      });
      numberTarget =
        importRow !== undefined
          ? { kind: 'import', registrationImport: importRow }
          : { kind: 'obligation', obligation };
      // "Add an earlier year" only for a year-keyed shape: a licence's periods and a one-time
      // filing have no earlier "year" to add. And only once its years carry dates: a year added
      // before the date it waits on is recorded (Florida with no registration date) would come
      // back undated, a year no tile shows.
      const yearKeyed = cycles.filter(
        (cycle) => cycle.isCurrent && isYearKeyed(cycle.cycleKey) && cycle.dueOn !== undefined,
      );
      // review-191 (F3): none before a first year counted from another filing's sending, and no
      // link once no year is left to offer, as with years not yet dated.
      const earlierYears = earlierYearChoices(
        cycles,
        occurrences.find((occurrence) => occurrence.firstPeriod !== undefined)?.firstPeriod,
      );
      earlier =
        yearKeyed.length > 0 && earlierYears.length > 0
          ? {
              kind: 'library',
              deadlineIndex: yearKeyed[0]?.deadlineIndex ?? 0,
              years: earlierYears,
            }
          : undefined;
      body = (
        <PanelBody
          row={row}
          next={next}
          tiles={tiles}
          numberLabel={numberLabel}
          number={number}
          numberTarget={numberTarget}
          earlier={earlier}
          canWrite={canOrganize}
          form={form}
          setForm={setForm}
          showAll={showAll}
          setShowAll={setShowAll}
          nameOf={nameOf}
          obligation={obligation}
          fiscalYearEnd={fye}
          {...(yearEndAsks !== undefined ? { yearEndAsks } : {})}
          ruleLink={ruleLink}
          correctedFrom={correctedFrom}
          history={history}
          preparation={preparation}
          desk={desk}
          {...(libraryNext !== undefined ? { deskNext: libraryNext } : {})}
          formLink={link(COMPLIANCE_ROUTES.filingForm(filingId))}
          sendLink={link(COMPLIANCE_ROUTES.filingSend(filingId))}
          restart={{
            onRestart: () => restartTracking.mutate(),
            pending: restartTracking.isPending,
            failure: restartTracking.error,
          }}
          library
          overdue={overdueCycles(obligation, cycles, occurrences, today)}
          alsoOpen={alsoOpen}
          yearPreparation={yearPreparation}
          {...(composition.followThrough !== undefined
            ? { followThrough: composition.followThrough }
            : {})}
          sentBy={sentBy}
          {...(onNavigate !== undefined ? { go: onNavigate } : {})}
        />
      );
    } else if (ownFiling !== undefined) {
      const detail = records.ownFilingDetails.get(ownFiling.filingId);
      const ownCycles: OwnFilingCycle[] = detail?.cycles ?? [];
      const editable = ownFilingStatus(ownFiling) === 'active';
      next = ownNextDue(ownFiling, ownNextDate(ownFiling), editable && canOrganize);
      tiles = ownYearTiles(ownFiling, ownCycles, vaultArtifacts, today);
      numberLabel = 'Number';
      number = ownFiling.number;
      numberTarget = editable ? { kind: 'own', filingId: ownFiling.filingId } : undefined;
      earlier = editable && ownFiling.cadence !== 'one_time' ? { kind: 'own' } : undefined;
      // CCM-R1-OWN-FILINGS-HISTORY (ruling 10, B3 (B)): the filing's own history keeps every change
      // to its number, so the panel shows them as a library filing's (rulings 8 and 9): the number
      // first entered beside a corrected one, and the corrections after the years. Its dates are
      // the organization's own, so no fiscal-year-end or date entry belongs here.
      const ownCorrectedFrom = correctedFromLine(detail?.firstEnteredNumber, number);
      const ownHistory = historyEntries({
        numberLabel,
        numberCorrections: ownNumberCorrections(detail?.numberCorrections ?? []),
        fiscalYearEnds: [],
        dueDateBases: [],
        events: [],
        nameOf,
      });
      body = (
        <PanelBody
          row={row}
          next={next}
          tiles={tiles}
          numberLabel={numberLabel}
          number={number}
          numberTarget={numberTarget}
          earlier={earlier}
          canWrite={canOrganize && editable}
          form={form}
          setForm={setForm}
          showAll={showAll}
          setShowAll={setShowAll}
          nameOf={nameOf}
          ownFilingId={ownFiling.filingId}
          ownCadence={ownFiling.cadence}
          ownNextDueOn={ownFiling.latestCycle?.dueOn}
          correctedFrom={ownCorrectedFrom}
          history={ownHistory}
          library={false}
        />
      );
    }
  }

  // The rule view: the same sheet, its header naming the filing it came from.
  const filingTitle = title;
  const backLink = showRule ? link(COMPLIANCE_ROUTES.filing(filingId)) : undefined;
  if (showRule && obligation !== undefined && body !== undefined && failed == null && !loading) {
    title = RULE_VIEW_TITLE;
    body = <RuleView obligation={obligation} />;
  }

  const targets = (composition.groups ?? []).filter(
    (candidate) =>
      candidate.groupId !== undefined &&
      (candidate.rows.length > 0 ||
        (row !== undefined && candidate.family === row.family && !candidate.own)),
  );

  return (
    <Sheet.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Sheet.Content size="lg" testId={PREFIX} initialFocus={false} aria-label={title || undefined}>
        <Sheet.Header>
          <div className="flex min-w-0 flex-1 items-start justify-between gap-12">
            <div className="flex min-w-0 flex-col gap-2">
              {showRule
                ? backLink !== undefined && (
                    <InPlaceAnchor
                      link={backLink}
                      className={cn(
                        'inline-flex w-fit items-center gap-4 rounded-xs text-s12 font-w500 text-text-tertiary hover:text-text-strong',
                        FOCUS_RING,
                      )}
                      testId="compliance-rule-view-back"
                    >
                      <Icon icon={ArrowLeft} size={14} aria-hidden />
                      Back to {filingTitle}
                    </InPlaceAnchor>
                  )
                : group !== undefined && (
                    <span
                      className="text-s12 font-w500 text-text-tertiary"
                      {...testIdProp(`${PREFIX}-eyebrow`)}
                    >
                      {group.label}
                    </span>
                  )}
              <Sheet.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
                {title}
              </Sheet.Title>
            </div>
            {row !== undefined && !showRule && (
              <PanelMenu
                row={row}
                current={group}
                targets={targets}
                canOrganize={canOrganize}
                onMove={(target) => moveFiling.mutate(target)}
                onNewGroup={() => setNewGroupOpen(true)}
                {...(canOrganize &&
                obligation !== undefined &&
                obligation.candidateState === 'confirmed' &&
                obligation.trackingStoppedAt === undefined
                  ? {
                      onStopTracking: () => {
                        stopTracking.reset();
                        setStopOpen(true);
                      },
                    }
                  : {})}
                {...(unlink !== undefined ? { onUnlink: unlink } : {})}
              />
            )}
          </div>
        </Sheet.Header>
        <Sheet.Body className="flex flex-col gap-28">{body}</Sheet.Body>
        {row !== undefined && (
          <NewGroupFromPanel
            row={row}
            open={newGroupOpen}
            pending={createGroup.isPending}
            onCreate={(name) => createGroup.mutate(name)}
            onClose={() => setNewGroupOpen(false)}
          />
        )}
        {obligation !== undefined && (
          <StopTrackingDialog
            filingName={filingTitle}
            {...(vendorLink !== undefined &&
            vendorLink.isCurrent &&
            vendorLink.renewalOn !== undefined
              ? { linkedRecord: linkedRecordTitle(vendorLink) }
              : {})}
            open={stopOpen}
            pending={stopTracking.isPending}
            failure={stopTracking.error}
            onStop={() => stopTracking.mutate()}
            onClose={() => setStopOpen(false)}
          />
        )}
      </Sheet.Content>
    </Sheet.Root>
  );
}

// ── The body: Next due, the number, History ────────────────────────────────────────────────────

/**
 * CCM-R1-LATE-FILINGS (D-CCM-62, frame 1a): each year read "Overdue", oldest first, as its own card
 * above "Next due": its date, the year it covers, its "Nothing recorded" line and its source;
 * then, where the rule's own source states one, its late fee in those words with their citation (L4);
 * then the year's own ways to act — its form, where the filing offers one, and recording it.
 */
function OverdueYears({
  years,
  obligation,
  canWrite,
  offersForm,
  yearPreparation,
  ruleLink,
  form,
  setForm,
  answeredBy,
  onDone,
}: {
  years: readonly RenewalCycle[];
  obligation: ComplianceObligation;
  canWrite: boolean;
  offersForm: boolean;
  /** The year's form: its case and its form, then the workbench on that year (`?cycle=`, O5). */
  yearPreparation?: YearPreparation;
  ruleLink?: InPlaceLink;
  form: OpenForm | null;
  setForm: (form: OpenForm | null) => void;
  answeredBy?: string;
  onDone: () => void;
}) {
  const state = obligation.jurisdictionName || obligation.jurisdictionCode;
  const lateFee = obligation.lateFee;
  const cards = years.map((cycle) => {
    const key = `overdue-${cycle.cycleId}`;
    const open = form?.kind === 'record' && form.tileKey === key ? form : undefined;
    const provenance = cycle.provenance ?? obligation.provenance;
    // With Y6 after it on a short year (CCM-BE24-B).
    const yearLine = fiscalYearEndedLine(cycle);
    return (
      <div key={key} className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-${key}`)}>
        <div className="flex flex-col gap-4 rounded-lg bg-surface-sunken px-16 py-12">
          <p className="text-s16 font-w600 tabular-nums text-text-strong">
            {longComplianceDate(cycle.dueOn)}
          </p>
          {yearLine !== undefined && (
            <p className="text-s13 leading-h15 text-text-secondary">{yearLine}</p>
          )}
          <p className="text-s13 leading-h15 text-text-secondary">{nothingRecordedLine(cycle)}</p>
          {provenance !== undefined && (
            <div className="pt-4">
              <RuleProvenanceLine provenance={provenance} citationLink={ruleLink} />
            </div>
          )}
        </div>
        {/* L4, after the dated block (whose last line cites the date), never inside it. */}
        {lateFee !== undefined && (
          <div
            className="flex flex-col gap-2 text-s13 leading-h15"
            {...testIdProp(`${PREFIX}-${key}-late-fee`)}
          >
            <p className="text-text-secondary">{lateFeeLead(state)}</p>
            <p className="text-text">“{lateFee.quotedSourceText}”</p>
            <p className="text-s12 text-text-tertiary">{lateFee.citation}</p>
          </div>
        )}
        {open !== undefined ? (
          <RecordFilingForm
            target={open.target}
            periodLine={open.periodLine}
            answeredBy={answeredBy}
            initial={open.initial}
            onDone={onDone}
          />
        ) : (
          canWrite && (
            <div className="flex flex-wrap gap-8">
              {offersForm && yearPreparation !== undefined && (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  loading={yearPreparation.preparingYear === cycle.cycleId}
                  onClick={() => yearPreparation.prepareYear(cycle)}
                  testId={`${PREFIX}-${key}-prepare`}
                >
                  {PREPARE_THE_FORM}
                </Button>
              )}
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() =>
                  setForm({
                    kind: 'record',
                    target: {
                      kind: 'renewal-cycle',
                      cycle,
                      receiptKind: receiptKindFor(
                        obligation.obligationClass,
                        isYearKeyed(cycle.cycleKey),
                      ),
                    },
                    periodLine: periodLineOf(cycle),
                    initial: 'confirmed',
                    tileKey: key,
                  })
                }
                testId={`${PREFIX}-${key}-record`}
              >
                Record this filing
              </Button>
            </div>
          )
        )}
      </div>
    );
  });
  return (
    <PanelSection title={OVERDUE} testId={`${PREFIX}-overdue`}>
      {cards}
    </PanelSection>
  );
}

/**
 * CCM-BE24-B (W2): under "It changed", on the day of the change, the earlier year end's last year and
 * the short year can both be open, each through its automatic extension. Each year after the first
 * shows here, oldest first, as its own card: its date with "Date passed", the year it covers (Y6 on
 * the short year), the basis line and its Change on the newest only (they name the year end in force),
 * the extension line and the source; then its own ways to act, as an overdue year's.
 */
function AlsoOpenYears({
  years,
  obligation,
  fiscalYearEnd,
  canWrite,
  offersForm,
  yearPreparation,
  ruleLink,
  form,
  setForm,
  answeredBy,
  onDone,
  changeForm,
}: {
  years: readonly RenewalCycle[];
  obligation: ComplianceObligation;
  fiscalYearEnd?: string;
  canWrite: boolean;
  offersForm: boolean;
  yearPreparation?: YearPreparation;
  ruleLink?: InPlaceLink;
  form: OpenForm | null;
  setForm: (form: OpenForm | null) => void;
  answeredBy?: string;
  onDone: () => void;
  changeForm: (change: BasisChange) => ReactNode;
}) {
  return years.map((cycle, index) => {
    const key = `open-${cycle.cycleId}`;
    const newest = index === years.length - 1;
    const basis = newest ? cycleBasisLine(obligation, cycle, fiscalYearEnd) : undefined;
    const change = newest ? basisChangeFor(cycle.dueDateBasis) : undefined;
    const yearLine = fiscalYearEndedLine(cycle);
    const extension = extensionLine(cycle.extension);
    const provenance = cycle.provenance ?? obligation.provenance;
    const open = form?.kind === 'record' && form.tileKey === key ? form : undefined;
    return (
      <div key={key} className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-${key}`)}>
        <div className="flex flex-col gap-4 rounded-lg bg-surface-sunken px-16 py-12">
          <p className="text-s16 font-w600 tabular-nums text-text-strong">
            {longComplianceDate(cycle.dueOn)}
            <span className="text-s13 font-w400 text-text-secondary">
              {' · '}
              {DATE_PASSED}
            </span>
          </p>
          {yearLine !== undefined && (
            <p className="text-s13 leading-h15 text-text-secondary">{yearLine}</p>
          )}
          {basis !== undefined && (
            <p className="text-s13 leading-h15 text-text-secondary">
              {basis}
              {canWrite && change !== undefined && (
                <>
                  {' · '}
                  <QuietLink
                    onClick={() => setForm({ kind: 'change', change })}
                    testId={`${PREFIX}-${key}-change`}
                  >
                    Change
                  </QuietLink>
                </>
              )}
            </p>
          )}
          {extension !== undefined && (
            <p className="text-s12 leading-h15 text-text-tertiary">{extension}</p>
          )}
          {provenance !== undefined && (
            <div className="pt-4">
              <RuleProvenanceLine provenance={provenance} citationLink={ruleLink} />
            </div>
          )}
        </div>
        {newest && form?.kind === 'change' ? (
          changeForm(form.change)
        ) : open !== undefined ? (
          <RecordFilingForm
            target={open.target}
            periodLine={open.periodLine}
            answeredBy={answeredBy}
            initial={open.initial}
            onDone={onDone}
          />
        ) : (
          canWrite && (
            <div className="flex flex-wrap gap-8">
              {offersForm && yearPreparation !== undefined && (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  loading={yearPreparation.preparingYear === cycle.cycleId}
                  onClick={() => yearPreparation.prepareYear(cycle)}
                  testId={`${PREFIX}-${key}-prepare`}
                >
                  {PREPARE_THE_FORM}
                </Button>
              )}
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() =>
                  setForm({
                    kind: 'record',
                    target: {
                      kind: 'renewal-cycle',
                      cycle,
                      receiptKind: receiptKindFor(obligation.obligationClass, true),
                    },
                    periodLine: periodLineOf(cycle),
                    initial: 'confirmed',
                    tileKey: key,
                  })
                }
                testId={`${PREFIX}-${key}-record`}
              >
                Record this filing
              </Button>
            </div>
          )
        )}
      </div>
    );
  });
}

function PanelBody({
  row,
  next,
  tiles,
  numberLabel,
  number,
  numberTarget,
  earlier,
  canWrite,
  form,
  setForm,
  showAll,
  setShowAll,
  nameOf,
  obligation,
  fiscalYearEnd,
  yearEndAsks,
  ownFilingId,
  ownCadence,
  ownNextDueOn,
  ruleLink,
  correctedFrom,
  history,
  preparation,
  desk,
  deskNext,
  formLink,
  sendLink,
  restart,
  library,
  overdue,
  alsoOpen = [],
  yearPreparation,
  followThrough,
  sentBy = () => undefined,
  go,
}: {
  row: FilingRow;
  next: NextDueView;
  tiles: YearTile[];
  numberLabel: string;
  number?: string;
  numberTarget?: NumberTarget;
  earlier?: { kind: 'library'; deadlineIndex: number; years: number[] } | { kind: 'own' };
  canWrite: boolean;
  form: OpenForm | null;
  setForm: (form: OpenForm | null) => void;
  showAll: boolean;
  setShowAll: (showAll: boolean) => void;
  nameOf: (userId: string) => string | undefined;
  obligation?: ComplianceObligation;
  fiscalYearEnd?: string;
  /** CCM-BE24-B: where moving the year end asks whether the earlier one was wrong (Y1). */
  yearEndAsks?: { organization: string; today: string };
  ownFilingId?: string;
  /** An own filing's cadence, and its open period's date: what "Change" opens on and moves. */
  ownCadence?: OwnFilingCadence;
  ownNextDueOn?: string;
  /** The rule's own view, where it is offered: the source line's citation goes there. */
  ruleLink?: InPlaceLink;
  /** "Corrected from …, which is what you first entered.", beside a corrected number (ruling 8). */
  correctedFrom?: string;
  /** The filing's corrections and changes, newest first (CCM-R1-INTAKE-HISTORY). */
  history: readonly HistoryEntry[];
  /** CCM-R1-DOCS: the form Givmo prepares for this filing, where it prepares one. */
  preparation?: PanelPreparation;
  /** CCM-R1-DESK-WEB: Givmo's filing desk on this filing's year, and that year's date. */
  desk?: PanelDesk;
  deskNext?: NextDate;
  /** The workbench's address, followed in place. */
  formLink?: InPlaceLink;
  /** The send review's address, followed in place (CCM-R1-SUBMIT-WEB). */
  sendLink?: InPlaceLink;
  /** CCM-R1-TRACKING-PANEL: S4, starting a stopped library filing's tracking again. */
  restart?: { onRestart: () => void; pending: boolean; failure: unknown };
  library: boolean;
  /** CCM-R1-LATE-FILINGS: the years read "Overdue", oldest first. */
  overdue?: readonly RenewalCycle[];
  /** CCM-BE24-B (W2): the years held open beside the first, oldest first. */
  alsoOpen?: readonly RenewalCycle[];
  /** CCM-R1-LATE-FILINGS: a year read overdue, prepared on its own year. */
  yearPreparation?: YearPreparation;
  /** CCM-R1-RESULTS-WEB: what came back after each year's latest send (a library filing's). */
  followThrough?: FollowThroughIndex;
  /** Who recorded an answer that came back. */
  sentBy?: (userId: string | undefined) => string | undefined;
  /** The host's navigation, for a step that opens the send page or the form. */
  go?: (to: string, search?: Readonly<Record<string, string>>) => void;
}) {
  const close = () => setForm(null);
  // Where focus goes once the fiscal year end's form closes (CCM-R1-INTAKE-FYE-A11Y): after a save,
  // to the date it moved; after a Change's Cancel, back to that Change. Never left on the page.
  const [focusAfter, setFocusAfter] = useState<'date' | 'change' | null>(null);
  useEffect(() => {
    if (focusAfter === null) return;
    const find = (id: string) =>
      document.querySelector<HTMLElement>(`[data-testid="${PREFIX}-${id}"]`);
    // Both are drawn whenever the form is: the Change sits in the basis line above it.
    ((focusAfter === 'change' ? find('change') : null) ?? find('next-lead'))?.focus();
    setFocusAfter(null);
  }, [focusAfter]);
  const yearEndSaved = () => {
    close();
    setFocusAfter('date');
  };
  const actionTarget = next.action?.target;
  const actionCycleId =
    actionTarget !== undefined && actionTarget.kind !== 'obligation'
      ? actionTarget.cycle.cycleId
      : next.servedCycleId;
  // A step after what came back opens the send page's review or the form, on its own year where it
  // isn't the next date's (`?cycle=`, CCM-R1-LATE-FILINGS).
  const filingId = row.ref.id;
  const stepTo = (stepGo: 'send-again' | 'prepare-corrected' | 'check', cycleId?: string) => {
    const year: Record<string, string> =
      cycleId !== undefined ? { [FILING_YEAR_SEARCH_KEY]: cycleId } : {};
    if (stepGo === 'prepare-corrected') go?.(COMPLIANCE_ROUTES.filingForm(filingId), year);
    else if (stepGo === 'send-again')
      go?.(COMPLIANCE_ROUTES.filingSend(filingId), { ...SEND_AGAIN_SEARCH, ...year });
    else go?.(COMPLIANCE_ROUTES.filingSend(filingId), year);
  };
  // CCM-R1-DESK-WEB (Q4, the 23:27 fold): while Givmo's desk has the filing's year, its status and
  // acts replace that year's usual next steps. Sending it again is the desk's row's N13 (desk-15's
  // rule), and the desk prepares the corrected filing (desk-16's W2). "Check what happened" stays:
  // saying what happened is the organization's (desk-14).
  const deskTakesStep = (stepGo: 'send-again' | 'prepare-corrected' | 'check', cycleId?: string) =>
    desk?.holds === true &&
    stepGo !== 'check' &&
    (cycleId === undefined || cycleId === desk.cycleId);
  const shown = showAll ? tiles : tiles.slice(0, TILES_SHOWN);
  const hidden = tiles.length - shown.length;
  const answeredBy = row.confirmationFrom;
  // CCM-R1-LATE-FILINGS (D-CCM-62, frame 3b): "Add an earlier year" whenever the read offers one, at
  // the foot of the filing's years, with nothing recorded or years folded alike.
  const addEarlier = canWrite && earlier !== undefined;
  // No heading over nothing (CCM-R1-SUBMIT-WEB; slice 3's built-panel note, frame 60): a filing
  // made once and never filed has no year, no earlier year to add and no change to list.
  const historyShows =
    shown.length > 0 ||
    hidden > 0 ||
    (form?.kind === 'earlier' && earlier !== undefined) ||
    addEarlier ||
    history.length > 0;

  const recordFor = (tile: YearTile): RecordTarget | undefined => {
    if (tile.target.kind === 'renewal-cycle' && obligation !== undefined) {
      return {
        kind: 'renewal-cycle',
        cycle: tile.target.cycle,
        receiptKind: receiptKindFor(
          obligation.obligationClass,
          isYearKeyed(tile.target.cycle.cycleKey),
        ),
      };
    }
    if (tile.target.kind === 'own-cycle' && ownFilingId !== undefined) {
      return { kind: 'own-cycle', filingId: ownFilingId, cycle: tile.target.cycle };
    }
    return undefined;
  };

  const changeForm = (change: BasisChange) =>
    change === 'fiscal-year-end' ? (
      <FiscalYearEndForm
        current={fiscalYearEnd}
        onSaved={yearEndSaved}
        {...(yearEndAsks !== undefined ? { asks: yearEndAsks } : {})}
        onCancel={() => {
          close();
          setFocusAfter('change');
        }}
      />
    ) : (
      <AnchorDateForm
        kind={change}
        obligationId={obligation?.obligationId}
        filingId={ownFilingId}
        ownCadence={ownCadence}
        initial={
          change === 'license-expiry'
            ? obligation?.licenseExpiresOn
            : change === 'registration-date'
              ? obligation?.registrationDate
              : ownNextDueOn
        }
        onDone={close}
      />
    );

  return (
    <>
      {/* CCM-OPS1-REPIN (frame 103): N36, what changed, and the act that moves the filing. */}
      {obligation !== undefined && <RuleChangeNotice obligation={obligation} canWrite={canWrite} />}

      {/* N41 (and P2's AI form) and N39, one block: how the filing came to be as it is. */}
      {obligation !== undefined && <FilingProvenance obligation={obligation} />}

      {/* CCM-R1-LATE-FILINGS (D-CCM-62): each year read "Overdue", above Next due, preparable and
          recordable from its own year. */}
      {overdue !== undefined && overdue.length > 0 && obligation !== undefined && (
        <OverdueYears
          years={overdue}
          obligation={obligation}
          canWrite={canWrite}
          offersForm={preparation?.offered === true}
          yearPreparation={yearPreparation}
          ruleLink={ruleLink}
          form={form}
          setForm={setForm}
          answeredBy={answeredBy}
          onDone={close}
        />
      )}

      <PanelSection title="Next due" testId={`${PREFIX}-next-due`}>
        <div
          className="flex flex-col gap-4 rounded-lg bg-surface-sunken px-16 py-12"
          {...testIdProp(`${PREFIX}-next-block`)}
        >
          {/* Focus lands here after a fiscal year end is saved (its date is what the save moved). */}
          <p
            tabIndex={-1}
            className={cn(
              'outline-none',
              next.dated
                ? 'text-s16 font-w600 tabular-nums text-text-strong'
                : 'text-s14 font-w600 text-text-strong',
            )}
            {...testIdProp(`${PREFIX}-next-lead`)}
          >
            {next.lead}
            {next.leadNote !== undefined && (
              <span
                className="text-s13 font-w400 text-text-secondary"
                {...testIdProp(`${PREFIX}-next-date-passed`)}
              >
                {' · '}
                {next.leadNote}
              </span>
            )}
          </p>
          {next.yearLine !== undefined && (
            <p
              className="text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-next-year`)}
            >
              {next.yearLine}
            </p>
          )}
          {next.basis !== undefined && (
            <p
              className="text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-basis`)}
            >
              {next.basis}
              {canWrite && next.change !== undefined && (
                <>
                  {' · '}
                  <QuietLink
                    onClick={() => setForm({ kind: 'change', change: next.change as BasisChange })}
                    testId={`${PREFIX}-change`}
                  >
                    Change
                  </QuietLink>
                </>
              )}
              {/* L3 (D-CCM-94's V2, REUSED): the record's date changes on the record's panel (V4). */}
              {canWrite &&
                next.recordDate === true &&
                next.linked !== undefined &&
                go !== undefined && (
                  <>
                    {' · '}
                    <QuietLink
                      onClick={() =>
                        go(COMPLIANCE_ROUTES.filings, {
                          [RECORD_SEARCH_KEY]: (next.linked as ObligationVendorLink).vendorId,
                        })
                      }
                      testId={`${PREFIX}-linked-change`}
                    >
                      Change
                    </QuietLink>
                  </>
                )}
            </p>
          )}
          {next.lines !== undefined && next.lines.length > 0 && (
            // S3 and N40's line, one block (REVIEW-19's F2): closer together than the block's lines.
            <div className="flex flex-col gap-2">
              {next.lines.map((line) => (
                <p
                  key={line}
                  className="text-s13 leading-h15 text-text-secondary"
                  {...testIdProp(`${PREFIX}-stopped-line`)}
                >
                  {line}
                </p>
              ))}
            </div>
          )}
          {next.aside !== undefined && (
            <p
              className={cn(
                'leading-h15',
                // An exception after the year's latest send reads at body weight (D-CCM-68).
                next.asideException === true ? 'text-s13 text-text' : 'text-s12 text-text-tertiary',
              )}
              {...testIdProp(`${PREFIX}-aside`)}
            >
              {next.aside}
            </p>
          )}
          {/* L3 (D-CCM-97): the record this filing is linked to and who linked them, then, where
              the date is the record's, that it is the organization's own (V2's line, REUSED). */}
          {next.linked !== undefined && obligation !== undefined && (
            <div className="flex flex-col gap-2 pt-4">
              <LinkedRecordLine
                obligationId={obligation.obligationId}
                link={next.linked}
                {...(go !== undefined
                  ? {
                      onOpenRecord: (vendorId: string) =>
                        go(COMPLIANCE_ROUTES.filings, { [RECORD_SEARCH_KEY]: vendorId }),
                    }
                  : {})}
              />
              {next.recordDate === true && (
                <p className="text-s13 leading-h15 text-text-secondary">{OWN_RECORD_LINE}</p>
              )}
            </div>
          )}
          {/* CCM-MOD5B (V8b): the organization's records of this filing's kind, named, never merged. */}
          {next.namesRecords === true && obligation !== undefined && (
            <RecordsNamingLine
              obligationId={obligation.obligationId}
              {...(go !== undefined
                ? {
                    onOpenRecord: (vendorId: string) =>
                      go(COMPLIANCE_ROUTES.filings, { [RECORD_SEARCH_KEY]: vendorId }),
                  }
                : {})}
            />
          )}
          {next.provenance !== undefined &&
            // Under a date question the line moves into the question, but only for a person who is
            // shown the question: a viewer still gets the rule's citation beside its basis (§3 row 19)
            // and the way to the rule's own view.
            (next.question === undefined || !canWrite || obligation === undefined) && (
              // Round 5 (M1): the citation stays with what it cites — the block's last quiet line.
              <div className="pt-4">
                <RuleProvenanceLine provenance={next.provenance} citationLink={ruleLink} />
                <SourceRecheckNote provenance={next.provenance} />
              </div>
            )}
        </div>
        {next.sentence ? (
          // D-CCM-19 §3 row 7: under the worked-out date and its basis, at body size.
          <ComplianceDisclosure surface="obligation-detail" />
        ) : (
          !library && (
            <p
              className="text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-own-line`)}
            >
              {OWN_FILING_LINE}
            </p>
          )
        )}
        {next.question !== undefined && canWrite && obligation !== undefined ? (
          next.question.kind === 'fiscal-year-end' ? (
            // The question stays until it is answered: no Cancel (it would do nothing).
            <FiscalYearEndForm current={fiscalYearEnd} onSaved={yearEndSaved} />
          ) : (
            <AnchorDateForm
              kind={next.question.kind}
              obligationId={obligation.obligationId}
              question={`Givmo needs one date from you to work out ${obligation.jurisdictionName || obligation.jurisdictionCode}’s next date.`}
              {...(next.question.note !== undefined ? { note: next.question.note } : {})}
              footer={
                next.provenance !== undefined ? (
                  // The re-check line goes wherever the source line goes, directly under it.
                  <div>
                    <RuleProvenanceLine provenance={next.provenance} citationLink={ruleLink} />
                    <SourceRecheckNote provenance={next.provenance} />
                  </div>
                ) : undefined
              }
            />
          )
        ) : form?.kind === 'change' && alsoOpen.length === 0 ? (
          changeForm(form.change)
        ) : form?.kind === 'record' && form.tileKey === undefined ? (
          <RecordFilingForm
            target={form.target}
            periodLine={form.periodLine}
            answeredBy={answeredBy}
            initial={form.initial}
            onDone={close}
          />
        ) : (
          canWrite &&
          next.action !== undefined && (
            <div className="flex flex-col gap-8">
              <div className="flex flex-wrap gap-8">
                {/* Frame 30: offered only before anything is prepared for this year, and not
                    while Givmo's desk has the filing, since the desk prepares it (Q12). */}
                {preparation?.offered === true && !preparation.started && desk?.holds !== true && (
                  <PrepareButton preparation={preparation} />
                )}
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  onClick={() =>
                    setForm({
                      kind: 'record',
                      target: (next.action as NonNullable<NextDueView['action']>).target,
                      periodLine: next.action?.periodLine,
                      initial: next.action?.initial ?? 'confirmed',
                    })
                  }
                  testId={`${PREFIX}-action`}
                >
                  {next.action.label}
                </Button>
              </div>
              {preparation?.offered === true && !preparation.started && desk?.holds !== true && (
                <PrepareLine />
              )}
            </div>
          )
        )}
        {/* CCM-R1-RESULTS-WEB (frames e1–e3): the step after an exception, in the action's place. */}
        {canWrite &&
          next.step !== undefined &&
          !deskTakesStep(next.step.go) &&
          go !== undefined &&
          next.question === undefined &&
          form?.kind !== 'change' && (
            <div>
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => stepTo(next.step?.go ?? 'send-again')}
                testId={`${PREFIX}-step`}
              >
                {next.step.label}
              </Button>
            </div>
          )}
        {canWrite && next.restartable === true && restart !== undefined && (
          // S4 (REVIEW-19's F2): it acts at once. A refusal or failure shows under it.
          <div className="flex flex-col gap-8">
            <div className="flex flex-wrap gap-8">
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                loading={restart.pending}
                onClick={restart.onRestart}
                testId={`${PREFIX}-start-tracking`}
              >
                {START_TRACKING_AGAIN}
              </Button>
            </div>
            {restart.failure !== null && restart.failure !== undefined && (
              <ErrorMessage testId={`${PREFIX}-start-tracking-failure`}>
                {serverMessage(restart.failure, RECORD_FAILED)}
              </ErrorMessage>
            )}
          </div>
        )}
        {alsoOpen.length > 0 && obligation !== undefined && (
          <AlsoOpenYears
            years={alsoOpen}
            obligation={obligation}
            fiscalYearEnd={fiscalYearEnd}
            canWrite={canWrite}
            offersForm={preparation?.offered === true}
            yearPreparation={yearPreparation}
            ruleLink={ruleLink}
            form={form}
            setForm={setForm}
            answeredBy={answeredBy}
            onDone={close}
            changeForm={changeForm}
          />
        )}
      </PanelSection>

      {/* CCM-R1-DESK-WEB (frame 21a): "Who files it", and the desk's status once it has it. */}
      {desk !== undefined && obligation !== undefined && (
        <PanelDeskRow
          panelDesk={desk}
          obligation={obligation}
          next={deskNext}
          onOpenForm={formLink?.onFollow}
          onReviewAndAuthorize={sendLink?.onFollow}
        />
      )}

      {/* Frame 49: once preparation has started, between Next due and the number. */}
      {preparation?.started === true && formLink !== undefined && (
        <PreparedFiling
          preparation={preparation}
          onOpen={formLink.onFollow}
          {...(sendLink !== undefined ? { onSend: sendLink.onFollow } : {})}
        />
      )}

      <PanelSection title={numberLabel} testId={`${PREFIX}-number`}>
        {form?.kind === 'number' && numberTarget !== undefined ? (
          <NumberForm target={numberTarget} label={numberLabel} current={number} onDone={close} />
        ) : (
          <>
            <div className="flex items-center gap-12">
              {number !== undefined ? (
                // The organization typed it, up to 200 characters with no space: it wraps anywhere
                // rather than scrolling the panel sideways at phone width.
                <span className="min-w-0 text-s14 tabular-nums text-text wrap-anywhere">
                  {number}
                </span>
              ) : (
                <span className="text-s14 text-text-tertiary">Not recorded</span>
              )}
              {canWrite && numberTarget !== undefined && (
                <QuietLink
                  onClick={() => setForm({ kind: 'number' })}
                  testId={`${PREFIX}-number-edit`}
                >
                  {number !== undefined ? 'Correct' : 'Add'}
                </QuietLink>
              )}
            </div>
            {/* CCM-WEB2's own line, under the number (ruling 8; frames b1, b3): the original stays
                visible beside its correction. */}
            {correctedFrom !== undefined && (
              <p
                className="-mt-4 text-s12 text-text-tertiary wrap-anywhere"
                {...testIdProp(`${PREFIX}-corrected-from`)}
              >
                {correctedFrom}
              </p>
            )}
          </>
        )}
      </PanelSection>

      {historyShows && (
        <PanelSection title="History" testId={`${PREFIX}-history`}>
          {shown.length > 0 && (
            // Round 6: one tile per period, newest first, titled by the period it covers.
            <ul className="flex flex-col gap-10">
              {shown.map((tile) => {
                const target = recordFor(tile);
                const open =
                  form?.kind === 'record' && form.tileKey === tile.key ? form : undefined;
                const record =
                  canWrite && target !== undefined && open === undefined
                    ? () =>
                        setForm({
                          kind: 'record',
                          target,
                          initial: 'confirmed',
                          periodLine:
                            target.kind === 'renewal-cycle'
                              ? periodLineOf(target.cycle)
                              : undefined,
                          tileKey: tile.key,
                        })
                    : undefined;
                // The year the block's own action already serves gets no second door from its tile.
                const servedAbove =
                  target !== undefined &&
                  target.kind !== 'obligation' &&
                  actionCycleId === target.cycle.cycleId;
                // CCM-R1-RESULTS-WEB (D-CCM-68, section 5): the year's latest send says what came
                // back, unless the register records the year filed; an exception's step is the
                // tile's link on a year "Next due" doesn't serve.
                const tileCycleId =
                  tile.target.kind === 'renewal-cycle' ? tile.target.cycle.cycleId : undefined;
                const sent =
                  obligation !== undefined &&
                  tile.target.kind !== 'own-cycle' &&
                  tile.act?.kind !== 'filed'
                    ? followThroughLine(
                        followThrough?.forYear(obligation.obligationId, tileCycleId),
                        sentBy,
                      )
                    : undefined;
                const exception = sent !== undefined && isException(sent);
                const stepGo: 'send-again' | 'prepare-corrected' | 'check' | undefined =
                  sent === undefined || !exception
                    ? undefined
                    : sent.kind === 'not-delivered'
                      ? 'send-again'
                      : sent.kind === 'answer'
                        ? 'prepare-corrected'
                        : 'check';
                const step =
                  stepGo !== undefined &&
                  tileCycleId !== undefined &&
                  !deskTakesStep(stepGo, tileCycleId) &&
                  !servedAbove &&
                  canWrite &&
                  go !== undefined
                    ? {
                        label:
                          stepGo === 'send-again'
                            ? SEND_IT_AGAIN
                            : stepGo === 'prepare-corrected'
                              ? PREPARE_THE_CORRECTED_FILING
                              : CHECK_WHAT_HAPPENED,
                        onClick: () => stepTo(stepGo, tileCycleId),
                      }
                    : undefined;
                return (
                  <YearTileView
                    key={tile.key}
                    tile={tile}
                    confirmationFrom={answeredBy}
                    nameOf={nameOf}
                    {...(sent !== undefined
                      ? {
                          sendLine: followThroughTileParts(sent, answeredBy ?? row.headline).join(
                            ' · ',
                          ),
                        }
                      : {})}
                    {...(step !== undefined ? { step } : {})}
                    {...(record !== undefined &&
                    tile.act === undefined &&
                    sent === undefined &&
                    tile.covered === undefined
                      ? { onAdd: record }
                      : {})}
                    {...(record !== undefined &&
                    tile.act?.kind === 'sent' &&
                    !servedAbove &&
                    !exception
                      ? { onAttach: record }
                      : {})}
                    {...(open !== undefined
                      ? {
                          recordForm: (
                            <RecordFilingForm
                              target={open.target}
                              periodLine={open.periodLine}
                              answeredBy={answeredBy}
                              initial={open.initial}
                              onDone={close}
                            />
                          ),
                        }
                      : {})}
                  />
                );
              })}
            </ul>
          )}
          {hidden > 0 ? (
            <div>
              <button
                type="button"
                aria-expanded={false}
                onClick={() => setShowAll(true)}
                className={cn(
                  'inline-flex items-center gap-4 rounded-xs text-s13 font-w500 text-text-secondary hover:text-text-strong',
                  FOCUS_RING,
                )}
                {...testIdProp(`${PREFIX}-show-earlier`)}
              >
                Show {hidden} earlier {hidden === 1 ? 'year' : 'years'}
                <Icon icon={ChevronDown} size={14} aria-hidden />
              </button>
              {/* Offered while earlier years are folded too (frame 3b). */}
              {addEarlier && (
                <div className="pt-8">
                  <QuietLink
                    onClick={() => {
                      setShowAll(true);
                      setForm({ kind: 'earlier' });
                    }}
                    testId={`${PREFIX}-add-earlier`}
                  >
                    Add an earlier year
                  </QuietLink>
                </div>
              )}
            </div>
          ) : form?.kind === 'earlier' && earlier !== undefined ? (
            earlier.kind === 'library' ? (
              <EarlierYearForm
                obligationId={obligation?.obligationId}
                deadlineIndex={earlier.deadlineIndex}
                years={earlier.years}
                onDone={close}
              />
            ) : (
              <EarlierYearForm filingId={ownFilingId} onDone={close} />
            )
          ) : (
            addEarlier && (
              <div>
                <QuietLink
                  onClick={() => setForm({ kind: 'earlier' })}
                  testId={`${PREFIX}-add-earlier`}
                >
                  Add an earlier year
                </QuietLink>
              </div>
            )
          )}
          {/* Rulings 8 and 9 (frames b1–b3): their own list after the years, newest first, so a
            correction never reads as a filing year. Each line wraps anywhere: the numbers and the
            person's own words are typed, and a pasted link otherwise scrolls the panel sideways at
            phone width (390 px: 65 px of sideways scroll before `wrap-anywhere`, 0 after). */}
          {history.length > 0 && (
            <div className="flex flex-col gap-8 pt-6" {...testIdProp(`${PREFIX}-changes`)}>
              <h4 className="text-s13 font-w600 text-text-strong">{CORRECTIONS_AND_CHANGES}</h4>
              <ul className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-subtle">
                {history.map((entry) => (
                  <li
                    key={entry.key}
                    className="flex flex-col gap-2 px-16 py-10"
                    {...testIdProp(`${PREFIX}-change-${entry.key}`)}
                  >
                    <p className="text-s13 text-text wrap-anywhere">{entry.what}</p>
                    <p className="text-s12 leading-h15 text-text-tertiary wrap-anywhere">
                      {entry.how}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </PanelSection>
      )}
    </>
  );
}
